import { useEffect } from "react";

// Performance optimization component
export const PerformanceOptimizer = () => {
  useEffect(() => {
    // Enable hardware acceleration for better animation performance
    const enableHardwareAcceleration = () => {
      // Add CSS classes for GPU acceleration
      document.documentElement.classList.add("motion-safe");

      // Optimize scroll performance
      let ticking = false;
      const updateScroll = () => {
        // Handle scroll-based animations efficiently
        ticking = false;
      };

      const requestTick = () => {
        if (!ticking) {
          requestAnimationFrame(updateScroll);
          ticking = true;
        }
      };

      window.addEventListener("scroll", requestTick, { passive: true });

      return () => {
        window.removeEventListener("scroll", requestTick);
      };
    };

    // Optimize animation performance
    const optimizeAnimations = () => {
      // Reduce motion for users who prefer it
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      if (prefersReducedMotion) {
        document.documentElement.classList.add("reduce-motion");
      }

      // Optimize for mobile devices
      const isMobile = window.innerWidth < 768;
      if (isMobile) {
        document.documentElement.classList.add("mobile-optimized");
      }
    };

    const cleanup = enableHardwareAcceleration();
    optimizeAnimations();

    return cleanup;
  }, []);

  return null;
};

// Enhanced hook for optimizing animation performance
export const useAnimationPerformance = () => {
  useEffect(() => {
    // Preload critical animations with requestAnimationFrame
    const preloadAnimations = () => {
      requestAnimationFrame(() => {
        const animatedElements = document.querySelectorAll(
          ".gpu-accelerated, [data-motion], .motion-optimized"
        );
        animatedElements.forEach((el) => {
          // Force GPU layer creation and optimize
          el.style.transform = "translateZ(0)";
          el.style.willChange = "transform, opacity";
          el.style.contain = "layout style paint";
          el.style.isolation = "isolate";
          // Trigger a reflow to create GPU layer
          el.offsetHeight;
        });
      });
    };

    // Optimized scroll handling with RAF
    let rafId;
    const handleScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        // Handle scroll-based animations
        rafId = null;
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Run preload after initial render with shorter delay
    const timer = setTimeout(preloadAnimations, 50);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
};

// Enhanced utility for reducing layout thrashing
export const batchDOMUpdates = (updates) => {
  // Use requestAnimationFrame to batch DOM updates
  requestAnimationFrame(() => {
    // Batch all updates in a single frame
    updates.forEach((update) => {
      if (typeof update === "function") {
        update();
      }
    });
  });
};

// Performance monitoring utility
export const measureAnimationPerformance = (callback) => {
  const start = performance.now();
  callback();
  const end = performance.now();
  console.log(`Animation performance: ${end - start}ms`);
};

// Utility for smooth scrolling
export const smoothScrollTo = (element, offset = 0) => {
  if (!element) return;

  const elementPosition = element.getBoundingClientRect().top;
  const offsetPosition = elementPosition + window.pageYOffset - offset;

  window.scrollTo({
    top: offsetPosition,
    behavior: "smooth",
  });
};
